import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Spinner, Form } from "react-bootstrap";
import Notifier from "../../components/Notifier";

import Api from "../../utils/Api";
import {
    subscribe,
    unsubscribe,
    getExistingSubscription,
    getBlockedReason,
    isStandalone,
    isIos
} from "../../utils/push";

const BLOCKED_MESSAGES = {
    NEEDS_INSTALL:
        'צריך להוסיף את האפליקציה למסך הבית: בספארי לוחצים על כפתור השיתוף ואז "הוספה למסך הבית", ופותחים משם.',
    UNSUPPORTED: "הדפדפן הזה לא תומך בהתראות.",
    DENIED: 'ההתראות נחסמו. יש לאשר אותן מחדש בהגדרות המכשיר, תחת התראות עבור "תקציב".',
    SERVER_NOT_CONFIGURED: "השרת לא מוגדר להתראות. חסרים מפתחות VAPID."
};

export default function PushNotifications({ isMobile }) {
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [subscribed, setSubscribed] = useState(false);
    const [serverEnabled, setServerEnabled] = useState(false);
    const [devices, setDevices] = useState([]);
    const [testMessage, setTestMessage] = useState("");
    const notifierRef = React.createRef();

    const cardStyle = {
        background: "white",
        boxShadow: "0 4px 8px 0 rgba(0,0,0,0.2)",
        borderRadius: "10px",
        width: isMobile ? "90%" : "50%",
        margin: "auto",
        marginTop: isMobile ? "0px" : "10px",
        padding: "20px"
    };

    const refreshData = useCallback(async () => {
        const { response } = await Api.getPushStatus();
        setServerEnabled(Boolean(response?.enabled));
        setDevices(response?.devices || []);
        setSubscribed(Boolean(await getExistingSubscription()));
        setLoading(false);
    }, []);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    const handleEnable = async () => {
        setBusy(true);
        try {
            const { success, reason } = await subscribe();
            if (success) {
                notifierRef?.current?.showSuccess("ההתראות הופעלו");
                await refreshData();
            } else {
                notifierRef?.current?.showError(BLOCKED_MESSAGES[reason] || "לא הצלחנו להפעיל התראות");
            }
        } catch (error) {
            console.log("failed enabling push notifications", error);
            notifierRef?.current?.showError("שגיאה בהפעלת ההתראות");
        } finally {
            setBusy(false);
        }
    };

    const handleDisable = async () => {
        setBusy(true);
        try {
            await unsubscribe();
            notifierRef?.current?.showSuccess("ההתראות כובו במכשיר הזה");
            await refreshData();
        } catch (error) {
            console.log("failed disabling push notifications", error);
            notifierRef?.current?.showError("שגיאה בכיבוי ההתראות");
        } finally {
            setBusy(false);
        }
    };

    const handleTest = async () => {
        setBusy(true);
        try {
            const { response } = await Api.sendTestPush(testMessage.trim());
            if (response?.sent > 0) {
                notifierRef?.current?.showSuccess(`נשלחה התראה ל-${response.sent} מכשירים`);
                setTestMessage("");
            } else {
                notifierRef?.current?.showError("לא נשלחה התראה. אין מכשירים רשומים.");
            }
        } catch (error) {
            console.log("failed sending test push", error);
            notifierRef?.current?.showError("שגיאה בשליחת ההתראה");
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: "flex", justifyContent: "center", marginTop: "40px" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    const blockedReason = getBlockedReason();

    return (
        <div style={cardStyle} dir="RTL">
            <h5 style={{ marginBottom: "15px" }}>התראות לנייד</h5>

            {!serverEnabled && (
                <div style={{ color: "#c0392b", marginBottom: "15px" }}>{BLOCKED_MESSAGES.SERVER_NOT_CONFIGURED}</div>
            )}

            {blockedReason && (
                <div
                    style={{
                        backgroundColor: "#fff8e1",
                        border: "1px solid #ffe082",
                        borderRadius: "6px",
                        padding: "12px",
                        marginBottom: "15px"
                    }}
                >
                    {BLOCKED_MESSAGES[blockedReason]}
                </div>
            )}

            {isIos() && isStandalone() && !blockedReason && (
                <div style={{ color: "#7f8c8d", marginBottom: "15px", fontSize: "0.9em" }}>
                    האפליקציה פתוחה ממסך הבית, אפשר להפעיל התראות.
                </div>
            )}

            <div style={{ display: "flex", gap: "10px", marginBottom: "15px" }}>
                {subscribed ? (
                    <Button variant="outline-danger" disabled={busy} onClick={handleDisable}>
                        כבה התראות במכשיר הזה
                    </Button>
                ) : (
                    <Button variant="primary" disabled={busy || Boolean(blockedReason) || !serverEnabled} onClick={handleEnable}>
                        הפעל התראות במכשיר הזה
                    </Button>
                )}
            </div>

            <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
                <Form.Control
                    type="text"
                    dir="RTL"
                    placeholder="הודעת בדיקה חופשית (אופציונלי)"
                    value={testMessage}
                    disabled={busy || !devices.length}
                    maxLength={200}
                    onChange={(event) => setTestMessage(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" && !busy && devices.length) {
                            handleTest();
                        }
                    }}
                />
                <Button
                    variant="outline-secondary"
                    style={{ whiteSpace: "nowrap" }}
                    disabled={busy || !devices.length}
                    onClick={handleTest}
                >
                    שלח בדיקה
                </Button>
            </div>

            <Table dir="RTL" style={{ marginBottom: 0 }}>
                <thead>
                    <tr>
                        <th>מכשיר</th>
                        <th style={{ width: "110px" }}>נרשם</th>
                        <th style={{ width: "110px" }}>התראה אחרונה</th>
                    </tr>
                </thead>
                <tbody>
                    {devices.length === 0 && (
                        <tr>
                            <td colSpan={3} style={{ color: "#7f8c8d" }}>
                                אין מכשירים רשומים
                            </td>
                        </tr>
                    )}
                    {devices.map((device) => (
                        <tr key={device.id}>
                            <td style={{ fontSize: "0.8em", wordBreak: "break-all" }}>{device.userAgent || "לא ידוע"}</td>
                            <td>{device.createdAt ? new Date(device.createdAt).toLocaleDateString("he-IL") : "-"}</td>
                            <td>
                                {device.lastSuccessAt ? new Date(device.lastSuccessAt).toLocaleDateString("he-IL") : "-"}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <Notifier ref={notifierRef} />
        </div>
    );
}
